
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const marketData = [
  { label: '自社', value: 94, color: 'bg-amber-400' },
  { label: '競合A', value: 42, color: 'bg-gray-600' },
  { label: '競合B', value: 28, color: 'bg-gray-700' }
];

const keyMetrics = [
  { icon: 'ri-map-pin-line', value: '94%', label: '主要商業エリアカバー率' },
  { icon: 'ri-eye-line', value: '820万', label: '月間ユニークリーチ' },
  { icon: 'ri-time-line', value: '24/7', label: '稼働時間' },
  { icon: 'ri-global-line', value: '48M', label: '年間観光客数' }
];

export default function MarketDominanceSection() {
  const [animatedReach, setAnimatedReach] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      let current = 0;
      const interval = setInterval(() => {
        current += 0.1;
        if (current >= 8.2) {
          setAnimatedReach(8.2);
          clearInterval(interval);
        } else {
          setAnimatedReach(current);
        }
      }, 20);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="dominance" className="relative bg-[#0A0A0A] py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-amber-400 text-xs tracking-[0.3em] uppercase">
            Data-Driven Dominance
          </span>
          <h2 className="text-4xl md:text-6xl font-black text-white mt-4">
            数値が証明する
            <br />
            ラスベガス<span className="text-amber-400">ビルボード市場</span>優位性
          </h2>
          <p className="text-white/60 text-lg mt-6 max-w-3xl mx-auto">
            屋外広告メディア資産として、主要エリアの視認性を独占。
            データに裏付けられた圧倒的な市場シェアとリーチ力。
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white/5 rounded-3xl p-8 border border-white/10"
          >
            <h3 className="text-white/60 text-sm mb-6">地理的カバレッジ</h3>
            <div className="relative aspect-square mb-6">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                <circle cx="100" cy="100" r="80" fill="none" stroke="#333" strokeWidth="2" />
                <circle cx="100" cy="100" r="60" fill="none" stroke="#333" strokeWidth="1" />
                <circle cx="100" cy="100" r="40" fill="none" stroke="#333" strokeWidth="1" />
                <circle cx="80" cy="70" r="12" fill="#F59E0B" opacity="0.8" />
                <circle cx="120" cy="80" r="15" fill="#F59E0B" opacity="0.9" />
                <circle cx="100" cy="110" r="18" fill="#F59E0B" />
                <circle cx="70" cy="120" r="10" fill="#F59E0B" opacity="0.7" />
                <circle cx="130" cy="130" r="14" fill="#F59E0B" opacity="0.85" />
                <text x="100" y="180" textAnchor="middle" fill="#666" fontSize="10">Las Vegas Metro Area</text>
              </svg>
            </div>
            <p className="text-white text-2xl font-bold text-center">
              主要商業エリア<span className="text-amber-400">94%</span>カバー
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 rounded-3xl p-8 border border-white/10 flex flex-col items-center justify-center"
          >
            <h3 className="text-white/60 text-sm mb-8">月間ユニークリーチ</h3>
            <div className="relative w-48 h-48">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="45" fill="none" stroke="#333" strokeWidth="8" />
                <motion.circle 
                  cx="50" 
                  cy="50" 
                  r="45" 
                  fill="none" 
                  stroke="#F59E0B" 
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray="283"
                  initial={{ strokeDashoffset: 283 }}
                  whileInView={{ strokeDashoffset: 283 * 0.18 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-white text-5xl font-black">{animatedReach.toFixed(1)}</span>
                <span className="text-white/60 text-sm">Million</span>
              </div>
            </div>
            <p className="text-white/60 text-sm mt-6">月間ユニークリーチ</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-white/5 rounded-3xl p-8 border border-white/10"
          >
            <h3 className="text-white/60 text-sm mb-8">市場シェア比較</h3>
            <div className="space-y-6">
              {marketData.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-2">
                    <span className={`text-sm ${index === 0 ? 'text-amber-400 font-semibold' : 'text-white/60'}`}>
                      {item.label}
                    </span>
                    <span className={`text-sm ${index === 0 ? 'text-amber-400 font-semibold' : 'text-white/60'}`}>
                      {item.value}%
                    </span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${item.value}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      className={`h-full ${item.color} rounded-full`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-white/40 text-xs mt-8 text-center">
              ※2024年Q4データ、第三者機関調査に基づく
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {keyMetrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-6"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-400/10 flex items-center justify-center">
                <i className={`${metric.icon} text-amber-400 text-xl`}></i>
              </div>
              <div className="text-white text-3xl font-black">{metric.value}</div>
              <div className="text-white/50 text-xs mt-2">{metric.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
